import type { SiteNavigationHandlers } from '@motorway/mw-dealer-shared-components/components';

import {
	triggerGuestSiteNavigationGAEvent,
	triggerSiteNavigationGAEvent,
	triggerUserSiteNavigationGAEvent,
} from 'Components/PageComposer/PageComposerSiteNavigation/PageComposerSiteNavigation.analytics';
import {
	triggerAccountSettingsLinkClickedEvent,
	triggerNavigationActiveBidsClickedEvent,
	triggerNavigationAvatarClickedEvent,
	triggerNavigationBidsClickedEvent,
	triggerNavigationBillingClickedEvent,
	triggerNavigationCancelledPurchasesClickedEvent,
	triggerNavigationCompletedPurchasesClickedEvent,
	triggerNavigationDealerDashboardClickedEvent,
	triggerNavigationDidntWinBidsClickedEvent,
	triggerNavigationHelpCenterClickedEvent,
	triggerNavigationHowItWorksClickedEvent,
	triggerNavigationInsightsClickedEvent,
	triggerNavigationLogoClickedEvent,
	triggerNavigationMarketplaceClickedEvent,
	triggerNavigationMotorwayMoveClickedEvent,
	triggerNavigationMotorwayPayClickedEvent,
	triggerNavigationMotorwayTradeClickedEvent,
	triggerNavigationPricingClickedEvent,
	triggerNavigationPurchasesClickedEvent,
	triggerNavigationSignOutClickedEvent,
	triggerNavigationTradeClickedEvent,
	triggerNavigationUnderOfferBidsClickedEvent,
	triggerNavigationWalletClickedEvent,
	triggerSignInLinkClicked,
	triggerSignUpLinkClicked,
} from 'Services/analytics/events';
import type { User } from 'Types/user';
import { LIST_TYPES } from 'Utilities/consts';
import { routeWithErrorHandling } from 'Utilities/routes.helpers';

import type {
	GenerateSiteNavigationHandlersProps,
	HandlerProps,
	ReactElementEvent,
} from './PageComposerSiteNavigation.types';

type ProductEventName = 'motorwayMove' | 'motorwayPay' | 'motorwayTrade' | 'products';

const handleProductClick = (eventName: ProductEventName, isGuest: boolean) => {
	if (isGuest) {
		triggerGuestSiteNavigationGAEvent(eventName);
		return;
	}

	triggerUserSiteNavigationGAEvent(eventName);
};

export const generateSiteNavigationHandlers = ({
	marketplaceButton,
	onSignOut,
	router,
	user,
}: GenerateSiteNavigationHandlersProps): SiteNavigationHandlers => {
	const handleDefaultClick = (e: ReactElementEvent, props: HandlerProps) => {
		e?.preventDefault();

		if (props.href) {
			window.open(props.href, '_blank'); // Assume all hrefs are external and should open in a new tab
			return;
		}

		void routeWithErrorHandling(router, props);
	};

	// TODO: Make sure this changes when the user is logged in/out.
	const isGuest = !user?.id;

	return {
		onAvatarClick: () => {
			triggerNavigationAvatarClickedEvent();
		},
		onBidsAndOffersActiveClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerNavigationActiveBidsClickedEvent();
			handleDefaultClick(e, props);
		},
		onBidsAndOffersClick: () => {
			triggerNavigationBidsClickedEvent();
		},
		onBidsAndOffersDidntWinClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerNavigationDidntWinBidsClickedEvent();
			handleDefaultClick(e, props);
		},
		onBidsAndOffersUnderOfferClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerNavigationUnderOfferBidsClickedEvent();
			handleDefaultClick(e, props);
		},
		onBillingClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerNavigationBillingClickedEvent();
			handleDefaultClick(e, props);
		},
		onDealerDashboardClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerNavigationDealerDashboardClickedEvent();
			handleDefaultClick(e, props);
		},
		onHelpCenterClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerNavigationHelpCenterClickedEvent();
			handleDefaultClick(e, props);
		},
		onHelpClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerNavigationHelpCenterClickedEvent();
			handleDefaultClick(e, props);
		},
		onHowItWorksClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerNavigationHowItWorksClickedEvent();
			handleDefaultClick(e, props);
		},
		onInsightsClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerNavigationInsightsClickedEvent();
			handleDefaultClick(e, props);
		},
		onLogoClick: (e: ReactElementEvent) => {
			const { as, href, onClick, replace } = marketplaceButton ?? {};

			e?.preventDefault();
			onClick?.();
			triggerSiteNavigationGAEvent({ eventName: 'logo', loggedIn: !isGuest });
			triggerNavigationLogoClickedEvent(isGuest);

			void routeWithErrorHandling(router, { as, path: href, replace });
		},
		onMarketplaceClick: (e: ReactElementEvent) => {
			const { as, href: path, onClick, replace } = marketplaceButton ?? {};
			const isSaleSlug = path?.includes(LIST_TYPES.auction) ?? false;

			e?.preventDefault();

			onClick?.();
			triggerNavigationMarketplaceClickedEvent({ isSaleSlug });

			void routeWithErrorHandling(router, { as, path, replace });
		},
		onPricingClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerNavigationPricingClickedEvent();
			handleDefaultClick(e, props);
		},
		onProductsClick: () => {
			handleProductClick('products', isGuest);
		},
		onProductsMotorwayMoveClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerNavigationMotorwayMoveClickedEvent();
			handleProductClick('motorwayMove', isGuest);
			handleDefaultClick(e, props);
		},
		onProductsMotorwayPayClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerNavigationMotorwayPayClickedEvent();
			handleProductClick('motorwayPay', isGuest);
			handleDefaultClick(e, props);
		},
		onProductsMotorwayTradeClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerNavigationMotorwayTradeClickedEvent();
			handleProductClick('motorwayTrade', isGuest);
			handleDefaultClick(e, props);
		},
		onPurchasesCancelledClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerNavigationCancelledPurchasesClickedEvent();
			handleDefaultClick(e, props);
		},
		onPurchasesClick: () => {
			triggerNavigationPurchasesClickedEvent();
		},
		onPurchasesCompleteClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerNavigationCompletedPurchasesClickedEvent();
			handleDefaultClick(e, props);
		},
		onSignInClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerSignInLinkClicked();
			handleDefaultClick(e, props);
		},
		onSignOutClick: (e: ReactElementEvent) => {
			triggerNavigationSignOutClickedEvent();
			e?.preventDefault();
			onSignOut?.();
		},
		onSignUpClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerSignUpLinkClicked();
			handleDefaultClick(e, props);
		},
		onTradeClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerNavigationTradeClickedEvent();
			handleDefaultClick(e, props);
		},
		onUserMenuHeaderClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerAccountSettingsLinkClickedEvent();
			handleDefaultClick(e, props);
		},
		onWalletClick: (e: ReactElementEvent, props: HandlerProps) => {
			triggerNavigationWalletClickedEvent();
			handleDefaultClick(e, props);
		},
	};
};

type DealerPermission = NonNullable<User['dealerPermissions']>[number];
type SiteNavigationPermissions = Array<DealerPermission | 'billing'>;

export const generateSiteNavigationPermissions = (user?: User): SiteNavigationPermissions => {
	// NOTE: `hasInvoiceAccess` exists on the session token instead of in the dealer permissions.
	// It is used for billing, so we are converting it to a 'billing' dealer permission for consistency.
	const dealerPermissions = user?.dealerPermissions ?? [];
	return user?.hasInvoiceAccess ? [...dealerPermissions, 'billing'] : dealerPermissions;
};
